import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { ReactNode, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  Button,
  Props as ButtonProps,
} from '@sorare/core/src/atoms/buttons/Button';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';

import { AddToWatchlist } from '../AddToWatchlist';
import type { AddToWatchlistButton_anyPlayer } from './__generated__/index.graphql';

type Props = {
  anyPlayer: AddToWatchlistButton_anyPlayer;
  ctaLabel?: ReactNode;
} & ButtonProps;

export const AddToWatchlistButton = ({
  anyPlayer,
  ctaLabel,
  fullWidth,
  ...props
}: Props) => {
  const [open, setOpen] = useState(false);
  const {
    flags: { useWatchlists = false },
  } = useFeatureFlags();
  if (!useWatchlists || !anyPlayer.commonPlayer) return null;
  return (
    <Dropdown
      gap={8}
      open={open}
      onClose={() => {
        setOpen(false);
      }}
      label={p => (
        <Button
          style={{ ...(!fullWidth && { flexShrink: 0 }) }}
          fullWidth={fullWidth}
          {...props}
          {...p}
          onClick={() => setOpen(true)}
        >
          {ctaLabel || (
            <FormattedMessage id="watchlist.addTo" defaultMessage="Add to" />
          )}
        </Button>
      )}
    >
      <AddToWatchlist anyPlayer={anyPlayer} onClose={() => setOpen(false)} />
    </Dropdown>
  );
};
AddToWatchlistButton.fragments = {
  anyPlayer: gql`
    fragment AddToWatchlistButton_anyPlayer on AnyPlayerInterface {
      slug
      displayName
      commonPlayer(rarity: common) {
        id
      }
      ...AddToWatchlist_anyPlayer
    }
    ${AddToWatchlist.fragments.anyPlayer}
  ` as TypedDocumentNode<AddToWatchlistButton_anyPlayer>,
};
